import { memo, useMemo } from "react";

import type * as ModelType from "@/components/features/items/discount/list/model/type";

import { DiscountTableRoot } from "./discount-table";
import { useDiscountRowsData } from "./hooks";

const DiscountAccordionIds = {
	ordinary: {
		id: "ordinary-discount",
		title: "通常ディスカウント",
	},
	longTermStay: {
		id: "long-term-stay-discount",
		title: "連泊ディスカウント",
	},
	multi: { id: "multi-discount", title: "複合ディスカウント" },
} as const;

type Props = {
	//
	onDeleteDiscount: (discountId: string) => void;

	ordinary: ModelType.QueryData["ordinary"];
	// ordinary
	onTransitionToEditOrdinaryDiscount: (discountID: string) => void;
	onTransitionToDuplicateOrdinaryDiscount: (duplicateDiscountID: string) => void;

	// longTermStay
	longTermStay: ModelType.QueryData["longTerm"];
	onTransitionToEditLongTermStayDiscount: (discountID: string) => void;
	onTransitionToDuplicateLongTermStayDiscount: (duplicateDiscountID: string) => void;

	// multi
	multi: ModelType.QueryData["multi"];
	onTransitionToEditMultiDiscount: (discountID: string) => void;
	onTransitionToDuplicateMultiDiscount: (duplicateDiscountID: string) => void;
};

export const DiscountList = memo(
	({
		//
		onDeleteDiscount,

		// ordinary
		ordinary,
		onTransitionToEditOrdinaryDiscount,
		onTransitionToDuplicateOrdinaryDiscount,

		// longTermStay
		longTermStay,
		onTransitionToEditLongTermStayDiscount,
		onTransitionToDuplicateLongTermStayDiscount,

		// multi
		multi,
		onTransitionToEditMultiDiscount,
		onTransitionToDuplicateMultiDiscount,
	}: Props) => {
		const { onGenerateRowsData } = useDiscountRowsData();
		/**
		 * OrdinaryDiscount の行データ
		 */
		const ordinaryDiscountRows = useMemo(
			() => onGenerateRowsData({ type: "ordinary", ordinaryList: ordinary }),
			[onGenerateRowsData, ordinary],
		);
		/**
		 * LongTermStayTerm の行データ
		 */
		const longTermStayDiscountRows = useMemo(
			() => onGenerateRowsData({ type: "longTermStay", longTermStayList: longTermStay }),
			[longTermStay, onGenerateRowsData],
		);
		const ordinaryAndLongTermStayDiscountIdWithNames = useMemo((): Array<{ id: string; name: string }> => {
			const ordinaryDiscountIds = ordinary.map((discount) => ({ id: discount.discountID, name: discount.name }));
			const longTermStayDiscountIds = longTermStay.map((discount) => ({
				id: discount.discountID,
				name: discount.name,
			}));
			return [...ordinaryDiscountIds, ...longTermStayDiscountIds];
		}, [longTermStay, ordinary]);
		/**
		 * MultiDiscount の行データ
		 */
		const multiDiscountRows = useMemo(
			() =>
				onGenerateRowsData({
					type: "multi",
					ordinaryAndLongTermStayDiscountIdWithNames,
					multiList: multi,
				}),
			[multi, onGenerateRowsData, ordinaryAndLongTermStayDiscountIdWithNames],
		);

		return (
			<div className="space-y-12">
				<DiscountTableRoot
					id={DiscountAccordionIds.ordinary.id}
					title={DiscountAccordionIds.ordinary.title}
					rows={ordinaryDiscountRows}
					onEditDiscount={onTransitionToEditOrdinaryDiscount}
					onDuplicateDiscount={onTransitionToDuplicateOrdinaryDiscount}
					onRemoveDiscount={onDeleteDiscount}
				/>

				<DiscountTableRoot
					id={DiscountAccordionIds.longTermStay.id}
					title={DiscountAccordionIds.longTermStay.title}
					rows={longTermStayDiscountRows}
					onEditDiscount={onTransitionToEditLongTermStayDiscount}
					onDuplicateDiscount={onTransitionToDuplicateLongTermStayDiscount}
					onRemoveDiscount={onDeleteDiscount}
				/>

				<DiscountTableRoot
					id={DiscountAccordionIds.multi.id}
					title={DiscountAccordionIds.multi.title}
					rows={multiDiscountRows}
					onEditDiscount={onTransitionToEditMultiDiscount}
					onDuplicateDiscount={onTransitionToDuplicateMultiDiscount}
					onRemoveDiscount={onDeleteDiscount}
				/>
			</div>
		);
	},
);

DiscountList.displayName = "DiscountList";
